import { PDFDocument, StandardFonts, rgb, PageSizes } from 'pdf-lib';
import { toast } from 'react-toastify';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from './errorHandler';

export interface DownloadJobDescriptionPdfArgs {
  tenantId: number | string;
  nodeId: number | string;
  peranName?: string;
  division?: string;
  year?: number;
  reportFileUrls?: string[];
  /**
   * Accent colour for the generated document as a hex string (e.g. "#A3C94F").
   * Omitted, the backend renders its standard navy.
   */
  themeColor?: string;
}

export function buildAosFilename(
  args: Pick<DownloadJobDescriptionPdfArgs, 'nodeId' | 'peranName' | 'division' | 'year'>,
): string {
  const { nodeId, peranName, division, year } = args;
  const tahun = year ?? new Date().getFullYear();
  const departemen = division ? division.charAt(0).toUpperCase() + division.slice(1).toLowerCase() : null;
  const parts: string[] = ['Dokumen AOS', String(tahun)];
  if (departemen) parts.push(departemen);
  parts.push(peranName ?? String(nodeId));
  return `${parts.join('_')}.pdf`;
}

async function fetchPdfBytes(url: string): Promise<ArrayBuffer | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('pdf')) return null;
    return await response.arrayBuffer();
  } catch {
    return null;
  }
}

async function appendReportAttachments(aosPdfBytes: ArrayBuffer, reportFileUrls: string[]): Promise<Uint8Array> {
  const aosPdf = await PDFDocument.load(aosPdfBytes);

  // Lampiran yang tak bisa diunduh atau rusak dilewati; yang valid tetap digabung.
  const templatePdfs: PDFDocument[] = [];
  for (const templateBytes of await Promise.all(reportFileUrls.map(fetchPdfBytes))) {
    if (!templateBytes) continue;
    try {
      templatePdfs.push(await PDFDocument.load(templateBytes));
    } catch (error) {
      console.warn('Lampiran report rusak, dilewati', error);
    }
  }
  if (templatePdfs.length === 0) return aosPdf.save();

  const font = await aosPdf.embedFont(StandardFonts.HelveticaBold);
  const [pageWidth, pageHeight] = PageSizes.A4;

  const coverPage = aosPdf.addPage([pageWidth, pageHeight]);
  const centerY = pageHeight / 2;

  coverPage.drawText('Lampiran 3', {
    x: pageWidth / 2 - font.widthOfTextAtSize('Lampiran 3', 18) / 2,
    y: centerY + 16,
    size: 18,
    font,
    color: rgb(0, 0, 0),
  });
  coverPage.drawText('Template Laporan', {
    x: pageWidth / 2 - font.widthOfTextAtSize('Template Laporan', 14) / 2,
    y: centerY - 8,
    size: 14,
    font,
    color: rgb(0, 0, 0),
  });

  for (const templatePdf of templatePdfs) {
    const copiedPages = await aosPdf.copyPages(templatePdf, templatePdf.getPageIndices());
    copiedPages.forEach(page => aosPdf.addPage(page));
  }

  return aosPdf.save();
}

// Gagal menggabung lampiran tidak menggagalkan unduhan: dokumen AOS dikirim tanpa lampiran.
async function buildFinalPdfBytes(aosPdfBytes: ArrayBuffer, reportFileUrls?: string[]): Promise<Uint8Array> {
  if (!reportFileUrls || reportFileUrls.length === 0) return new Uint8Array(aosPdfBytes);
  try {
    return await appendReportAttachments(aosPdfBytes, reportFileUrls);
  } catch (error) {
    console.warn('Gagal menggabung lampiran report, mengunduh dokumen AOS tanpa lampiran', error);
    return new Uint8Array(aosPdfBytes);
  }
}

// Dengan responseType 'arraybuffer', body error server berupa ArrayBuffer; ubah ke JSON agar pesannya terbaca errorHandler.
function decodeErrorBody(error: unknown): void {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (!response || Object.prototype.toString.call(response.data) !== '[object ArrayBuffer]') return;
  try {
    response.data = JSON.parse(new TextDecoder().decode(response.data as ArrayBuffer));
  } catch {
    response.data = undefined;
  }
}

function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export async function downloadJobDescriptionPdf(args: DownloadJobDescriptionPdfArgs): Promise<void> {
  const { tenantId, nodeId, peranName, division, year, reportFileUrls, themeColor } = args;
  try {
    // The query param is only appended when a colour was chosen, so the
    // default download keeps hitting the plain URL.
    const themeQuery = themeColor ? `?theme_color=${encodeURIComponent(themeColor)}` : '';
    const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/node/${nodeId}/download-pdf${themeQuery}`;
    const response = await axiosInstance.get(url, { responseType: 'arraybuffer' });

    const finalBytes = await buildFinalPdfBytes(response.data, reportFileUrls);
    saveBlob(
      new Blob([finalBytes], { type: 'application/pdf' }),
      buildAosFilename({ nodeId, peranName, division, year }),
    );
    toast.success('Dokumen AOS berhasil diunduh');
  } catch (error) {
    decodeErrorBody(error);
    errorHandler(error);
  }
}
